export type PermissionActionDefinition = {
  name: string;
  description: string;
};

export type PermissionResourceDefinition = {
  name: string;
  code: string;
  type: 'MENU';
  description: string;
  actions: PermissionActionDefinition[];
};

import { ATTENDANCE_CENTER_PERMISSION_RESOURCES } from './attendanceDefinitions';
import { PAYROLL_CENTER_PERMISSION_RESOURCES } from './payrollDefinitions';
import { PERFORMANCE_CENTER_PERMISSION_RESOURCES } from './performanceDefinitions';
import { SOCIAL_INSURANCE_CENTER_PERMISSION_RESOURCES } from './socialInsuranceDefinitions';

const action = (
  name: string,
  description: string,
): PermissionActionDefinition => ({
  name,
  description,
});

export const EMPLOYEE_CENTER_PERMISSION_RESOURCES = [
  {
    name: '员工工作台',
    code: 'employee-center-workbench',
    type: 'MENU',
    description: '控制员工工作台页面访问、待办入口和风险下钻',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('open-todo', '打开待办'),
      action('open-risk-drilldown', '打开风险下钻'),
    ],
  },
  {
    name: '我的审批',
    code: 'employee-center-approval',
    type: 'MENU',
    description: '控制员工中心我的审批页面访问、审批任务查询和业务单据回跳',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('open-source', '回跳业务单据'),
    ],
  },
  {
    name: '员工花名册',
    code: 'employee-center-employees',
    type: 'MENU',
    description: '控制员工花名册页面访问、员工档案入口和花名册操作',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('create-employee', '新增员工'),
      action('view-profile', '查看档案'),
      action('quick-edit', '快速编辑'),
      action('edit-basic-info', '编辑基础信息'),
      action('edit-extended-profile', '编辑扩展档案'),
      action('create-contract', '新增合同'),
      action('edit-contract', '编辑合同'),
      action('renew-contract', '续签合同'),
      action('terminate-contract', '终止合同'),
      action('create-certificate', '新增证书'),
      action('edit-certificate', '编辑证书'),
      action('create-attachment', '新增附件'),
      action('edit-attachment', '编辑附件'),
      action('delete-attachment', '删除附件'),
      action('preview-attachment', '预览附件'),
      action('download-attachment', '下载附件'),
      action('initiate-change', '发起异动'),
      action('initiate-regularization', '发起转正'),
      action('initiate-resignation', '发起离职'),
      action('move-to-recycle-bin', '移入回收站'),
      action('view-recycle-bin', '查看回收站'),
      action('restore-employee', '恢复员工'),
      action('import-roster', '导入花名册'),
      action('export-current-columns', '当前列导出'),
      action('export-all-fields', '全字段导出'),
      action('export-selected', '导出所选'),
      action('mark-material-reminder', '标记资料提醒'),
      action('request-profile-completion', '发起资料补充'),
      action('update-portal-status', '更新员工端状态'),
    ],
  },
  {
    name: '待入职',
    code: 'employee-center-onboarding',
    type: 'MENU',
    description: '控制入职办理页面访问和流程操作',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('create-onboarding', '新建待入职'),
      action('copy-onboarding', '复制待入职'),
      action('edit-onboarding', '编辑待入职'),
      action('edit-materials', '编辑资料'),
      action('submit-onboarding', '提交入职'),
      action('complete-onboarding', '办理入职'),
      action('retry-effect', '重试生效'),
      action('cancel-effect', '取消待生效'),
      action('link-employee', '关联员工'),
      action('cancel-onboarding', '取消入职'),
      action('remove-order', '删除单据'),
      action('export-onboarding', '导出入职'),
    ],
  },
  {
    name: '转正',
    code: 'employee-center-regularization',
    type: 'MENU',
    description: '控制转正办理页面访问和流程操作',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('initiate-regularization', '发起转正'),
      action('edit-regularization', '编辑转正'),
      action('submit-regularization', '提交转正'),
      action('complete-regularization', '完成转正'),
      action('retry-effect', '重试生效'),
      action('cancel-effect', '取消待生效'),
      action('remove-order', '删除单据'),
      action('export-regularization', '导出转正'),
    ],
  },
  {
    name: '离职',
    code: 'employee-center-resignation',
    type: 'MENU',
    description: '控制离职办理页面访问和流程操作',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('create-resignation', '新建离职'),
      action('edit-resignation', '编辑离职'),
      action('submit-resignation', '提交离职'),
      action('complete-resignation', '完成离职'),
      action('retry-effect', '重试生效'),
      action('cancel-effect', '取消待生效'),
      action('remove-order', '删除单据'),
      action('maintain-handover', '维护交接'),
      action('view-handover', '查看交接'),
      action('create-handover-item', '新增交接项'),
      action('edit-handover-item', '编辑交接项'),
      action('complete-handover-item', '完成交接项'),
      action('delete-handover-item', '删除交接项'),
      action('upload-handover-attachment', '上传交接附件'),
      action('preview-handover-attachment', '预览交接附件'),
      action('download-handover-attachment', '下载交接附件'),
      action('export-resignation', '导出离职'),
    ],
  },
  {
    name: '岗位配置',
    code: 'employee-center-position-settings',
    type: 'MENU',
    description: '控制岗位配置页面访问、岗位查询和岗位维护',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('create-position', '新建岗位'),
      action('edit-position', '编辑岗位'),
      action('delete-position', '删除岗位'),
    ],
  },
  {
    name: '职级管理',
    code: 'employee-center-job-level-settings',
    type: 'MENU',
    description: '控制职级管理页面访问、职级查询和职级维护',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('create-job-level', '新建职级'),
      action('edit-job-level', '编辑职级'),
      action('delete-job-level', '删除职级'),
    ],
  },
  {
    name: '工作地点',
    code: 'employee-center-work-location-settings',
    type: 'MENU',
    description: '控制工作地点页面访问、工作地点查询和工作地点维护',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('create-work-location', '新建工作地点'),
      action('edit-work-location', '编辑工作地点'),
      action('delete-work-location', '删除工作地点'),
    ],
  },
  {
    name: '员工异动',
    code: 'employee-center-employee-change',
    type: 'MENU',
    description: '控制员工异动页面访问和流程操作',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('create-change', '新建异动'),
      action('edit-change', '编辑异动'),
      action('submit-change', '提交异动'),
      action('complete-change', '完成异动'),
      action('retry-effect', '重试生效'),
      action('cancel-effect', '取消待生效'),
      action('remove-order', '删除单据'),
      action('export-change', '导出异动'),
    ],
  },
  {
    name: '员工操作记录',
    code: 'employee-center-lifecycle-audits',
    type: 'MENU',
    description: '控制员工操作记录页面访问和导出',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('view-detail', '查看详情'),
      action('view-source', '查看来源'),
      action('export-audit', '导出审计'),
    ],
  },
  {
    name: '劳动关系',
    code: 'employee-center-labor-relations',
    type: 'MENU',
    description: '控制劳动关系页面访问和合同材料操作',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('view-contract-detail', '查看合同详情'),
      action('view-employee-profile', '查看员工档案'),
      action('create-contract', '新增合同'),
      action('change-contract', '变更合同'),
      action('renew-contract', '续签合同'),
      action('terminate-contract', '终止合同'),
      action('upload-contract-attachment', '上传合同附件'),
      action('initiate-regularization', '发起转正'),
      action('view-regularization', '查看转正'),
      action('edit-regularization', '编辑转正'),
      action('export-contracts', '导出合同'),
      action('export-probations', '导出试用期'),
    ],
  },
  {
    name: '离职交接模板',
    code: 'employee-center-handover-templates',
    type: 'MENU',
    description: '控制交接模板页面访问和启停操作',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('create-template', '新建模板'),
      action('edit-template', '编辑模板'),
      action('delete-template', '删除模板'),
    ],
  },
  {
    name: '证明申请处理',
    code: 'employee-center-certificate-requests',
    type: 'MENU',
    description: '控制 HR 证明申请处理页面访问和出具操作',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('view-detail', '查看详情'),
      action('accept-request', '接单'),
      action('reject-request', '退回申请'),
      action('issue-certificate', '出具证明'),
      action('upload-issued-file', '上传出具文件'),
      action('preview-issued-file', '预览出具文件'),
      action('download-issued-file', '下载出具文件'),
      action('export-requests', '导出证明申请'),
    ],
  },
  {
    name: '员工关系分析',
    code: 'employee-center-relations-analysis',
    type: 'MENU',
    description: '控制员工关系分析页面访问和查询',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('open-drilldown', '打开下钻'),
      action('open-risk-action', '打开风险动作'),
    ],
  },
  {
    name: '审批流程设置',
    code: 'employee-center-workflow-settings',
    type: 'MENU',
    description: '控制员工中心审批流程设置页面访问和流程启停保存',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('configure-workflow', '设置审批流程'),
      action('save-workflow-settings', '保存审批流程设置'),
    ],
  },
  {
    name: '员工提醒设置',
    code: 'employee-center-reminder-settings',
    type: 'MENU',
    description: '控制转正和合同到期提前提醒天数配置',
    actions: [
      action('view', '查看'),
      action('query', '查询'),
      action('save-reminder-settings', '保存提醒设置'),
    ],
  },
] as const satisfies readonly PermissionResourceDefinition[];

export const PERMISSION_RESOURCE_CODES = {
  workbench: 'employee-center-workbench',
  approval: 'employee-center-approval',
  employees: 'employee-center-employees',
  onboarding: 'employee-center-onboarding',
  regularization: 'employee-center-regularization',
  resignation: 'employee-center-resignation',
  positionSettings: 'employee-center-position-settings',
  jobLevelSettings: 'employee-center-job-level-settings',
  workLocationSettings: 'employee-center-work-location-settings',
  employeeChange: 'employee-center-employee-change',
  lifecycleAudits: 'employee-center-lifecycle-audits',
  laborRelations: 'employee-center-labor-relations',
  handoverTemplates: 'employee-center-handover-templates',
  certificateRequests: 'employee-center-certificate-requests',
  relationsAnalysis: 'employee-center-relations-analysis',
  workflowSettings: 'employee-center-workflow-settings',
  reminderSettings: 'employee-center-reminder-settings',
  myOverview: 'attendance-center-my-overview',
  myRecords: 'attendance-center-my-records',
  myRequests: 'attendance-center-my-requests',
  myLeaveBalances: 'attendance-center-my-leave-balances',
  myMonthly: 'attendance-center-my-monthly',
  teamOverview: 'attendance-center-team-overview',
  teamSchedules: 'attendance-center-team-schedules',
  teamExceptions: 'attendance-center-team-exceptions',
  teamMonthly: 'attendance-center-team-monthly',
  attendanceWorkbench: 'attendance-center-workbench',
  attendanceApproval: 'attendance-center-approval',
  records: 'attendance-center-records',
  dailyResults: 'attendance-center-daily-results',
  monthlySummaries: 'attendance-center-monthly-summaries',
  operationAudits: 'attendance-center-operation-audits',
  leaveRequests: 'attendance-center-leave-requests',
  overtimeRequests: 'attendance-center-overtime-requests',
  punchRequests: 'attendance-center-punch-requests',
  shifts: 'attendance-center-shifts',
  rules: 'attendance-center-rules',
  groups: 'attendance-center-groups',
  schedules: 'attendance-center-schedules',
  holidayPlans: 'attendance-center-holiday-plans',
  leaveBalances: 'attendance-center-leave-balances',
  leaveGrantPolicies: 'attendance-center-leave-grant-policies',
  settings: 'attendance-center-settings',
  attendanceWorkflowSettings: 'attendance-center-workflow-settings',
  payrollWorkbench: 'payroll-center-workbench',
  payrollApproval: 'payroll-center-approval',
  payrollPlans: 'payroll-center-plans',
  payrollItems: 'payroll-center-items',
  payrollInputs: 'payroll-center-inputs',
  payrollSourceBatches: 'payroll-center-source-batches',
  payrollTaxSimulation: 'payroll-center-tax-simulation',
  payrollCostAllocation: 'payroll-center-cost-allocation',
  payrollProfiles: 'payroll-center-profiles',
  payrollDeductions: 'payroll-center-deductions',
  payrollAdjustments: 'payroll-center-adjustments',
  payrollAdjustmentAnalysis: 'payroll-center-adjustment-analysis',
  payrollRuns: 'payroll-center-runs',
  payrollSlips: 'payroll-center-slips',
  payrollMySlips: 'payroll-center-my-slips',
  payrollTeamSummary: 'payroll-center-team-summary',
  payrollAudits: 'payroll-center-audits',
  payrollWorkflowSettings: 'payroll-center-workflow-settings',
  socialInsuranceMy: 'social-insurance-center-my',
  socialInsuranceTeam: 'social-insurance-center-team',
  socialInsuranceWorkbench: 'social-insurance-center-workbench',
  socialInsuranceApproval: 'social-insurance-center-approval',
  socialInsurancePolicies: 'social-insurance-center-policies',
  socialInsuranceAccounts: 'social-insurance-center-accounts',
  socialInsuranceChanges: 'social-insurance-center-changes',
  socialInsuranceBills: 'social-insurance-center-bills',
  socialInsuranceAudits: 'social-insurance-center-audits',
  socialInsuranceWorkflowSettings: 'social-insurance-center-workflow-settings',
  performanceOverview: 'performance-center-overview',
  performanceApproval: 'performance-center-approval',
  performanceMy: 'performance-center-my',
  performanceTeam: 'performance-center-team',
  performanceProfiles: 'performance-center-profiles',
  performanceCycles: 'performance-center-cycles',
  performanceCycleTemplates: 'performance-center-cycle-templates',
  performanceGoals: 'performance-center-goals',
  performanceReviews: 'performance-center-reviews',
  performanceScoreTemplates: 'performance-center-score-templates',
  performanceCalibrationBatches: 'performance-center-calibration-batches',
  performanceInterviews: 'performance-center-interviews',
  performanceResults: 'performance-center-results',
  performanceAudits: 'performance-center-audits',
  performanceGradeRules: 'performance-center-grade-rules',
  performanceWorkflowSettings: 'performance-center-workflow-settings',
} as const;

type ResourceCode =
  | (typeof EMPLOYEE_CENTER_PERMISSION_RESOURCES)[number]['code']
  | (typeof ATTENDANCE_CENTER_PERMISSION_RESOURCES)[number]['code']
  | (typeof PAYROLL_CENTER_PERMISSION_RESOURCES)[number]['code']
  | (typeof SOCIAL_INSURANCE_CENTER_PERMISSION_RESOURCES)[number]['code']
  | (typeof PERFORMANCE_CENTER_PERMISSION_RESOURCES)[number]['code'];
type ResourceKey = keyof typeof PERMISSION_RESOURCE_CODES;

type AllPermissionResources =
  | (typeof EMPLOYEE_CENTER_PERMISSION_RESOURCES)[number]
  | (typeof ATTENDANCE_CENTER_PERMISSION_RESOURCES)[number]
  | (typeof PAYROLL_CENTER_PERMISSION_RESOURCES)[number]
  | (typeof SOCIAL_INSURANCE_CENTER_PERMISSION_RESOURCES)[number]
  | (typeof PERFORMANCE_CENTER_PERMISSION_RESOURCES)[number];
type ActionMapForResource<T extends ResourceCode> = {
  [ActionName in Extract<
    AllPermissionResources,
    { code: T }
  >['actions'][number]['name']]: ActionName;
};

export const PERMISSION_ACTION_CODES_BY_RESOURCE = Object.fromEntries(
  [
    ...EMPLOYEE_CENTER_PERMISSION_RESOURCES,
    ...ATTENDANCE_CENTER_PERMISSION_RESOURCES,
    ...PAYROLL_CENTER_PERMISSION_RESOURCES,
    ...SOCIAL_INSURANCE_CENTER_PERMISSION_RESOURCES,
    ...PERFORMANCE_CENTER_PERMISSION_RESOURCES,
  ].map((resource) => [
    resource.code,
    Object.fromEntries(resource.actions.map((item) => [item.name, item.name])),
  ]),
) as {
  [Code in ResourceCode]: ActionMapForResource<Code>;
};

export const PERMISSION_ACTION_CODES = Object.fromEntries(
  Object.entries(PERMISSION_RESOURCE_CODES).map(([key, resourceCode]) => [
    key,
    PERMISSION_ACTION_CODES_BY_RESOURCE[resourceCode],
  ]),
) as {
  [Key in ResourceKey]: ActionMapForResource<
    (typeof PERMISSION_RESOURCE_CODES)[Key]
  >;
};

export type PermissionResourceCode =
  (typeof PERMISSION_RESOURCE_CODES)[keyof typeof PERMISSION_RESOURCE_CODES];

export const BUSINESS_CENTER_PERMISSION_RESOURCES = [
  ...PAYROLL_CENTER_PERMISSION_RESOURCES,
  ...SOCIAL_INSURANCE_CENTER_PERMISSION_RESOURCES,
  ...PERFORMANCE_CENTER_PERMISSION_RESOURCES,
] as const;

export const MANAGEMENT_CENTER_PERMISSION_RESOURCES =
  BUSINESS_CENTER_PERMISSION_RESOURCES.filter(
    (resource) =>
      ![
        'payroll-center-my-slips',
        'social-insurance-center-my',
        'performance-center-my',
      ].includes(resource.code),
  );
